/* Variables and functions for CSS-in-JS use */
import { Neutral1000 } from "../../tokens/js/colors";
import { PaddingSpacing2Xl, PaddingSpacing2Xs, PaddingSpacing3Xl, PaddingSpacing3Xs, PaddingSpacing4Xl, PaddingSpacingL, PaddingSpacingM, PaddingSpacingMl, PaddingSpacingS, PaddingSpacingSm, PaddingSpacingXl, PaddingSpacingXs } from "../../tokens/js/mobile";
import { PaddingSpacing2Xl as PaddingSpacing2XlTablet, PaddingSpacing2Xs as PaddingSpacing2XsTablet, PaddingSpacing3Xl as PaddingSpacing3XlTablet, PaddingSpacing3Xs as PaddingSpacing3XsTablet, PaddingSpacing4Xl as PaddingSpacing4XlTablet, PaddingSpacingL as PaddingSpacingLTablet, PaddingSpacingM as PaddingSpacingMTablet, PaddingSpacingMl as PaddingSpacingMlTablet, PaddingSpacingS as PaddingSpacingSTablet, PaddingSpacingSm as PaddingSpacingSmTablet, PaddingSpacingXl as PaddingSpacingXlTablet, PaddingSpacingXs as PaddingSpacingXsTablet } from "../../tokens/js/tablet";
import { PaddingSpacing2Xl as PaddingSpacing2XlDesktop, PaddingSpacing2Xs as PaddingSpacing2XsDesktop, PaddingSpacing3Xl as PaddingSpacing3XlDesktop, PaddingSpacing3Xs as PaddingSpacing3XsDesktop, PaddingSpacing4Xl as PaddingSpacing4XlDesktop, PaddingSpacingL as PaddingSpacingLDesktop, PaddingSpacingM as PaddingSpacingMDesktop, PaddingSpacingMl as PaddingSpacingMlDesktop, PaddingSpacingS as PaddingSpacingSDesktop, PaddingSpacingSm as PaddingSpacingSmDesktop, PaddingSpacingXl as PaddingSpacingXlDesktop, PaddingSpacingXs as PaddingSpacingXsDesktop } from "../../tokens/js/desktop";
import { UpsunTheme } from "../themes";
import colorsLight from "./colors-light";
import colorsDark from "./colors-dark";

// Media queries
// Add values as needed
const breakpoints = [480, 667, 768, 992, 1280, 1440, 1600];

const names = [`xs`, `sm`, `md`, `lg`, `xl`, `xxl`, `xxxl`];

type MediaQueries = { [key: string] : string };

type ZLayer = "hidden" | "main" | "over-main" | "header" | "modal";

type SpacingToken = 
  "3xs" | "2xs" | "xs" | "s" | "sm" | "m" | "ml" | "l" | "xl" | "2xl" | "3xl" | "4xl" | "0" | 0;

type SpacingProp = 
  "margin" | "padding" |
  "margin-block" | "margin-block-start" | "margin-block-end" |
  "margin-inline" | "margin-inline-start" | "margin-inline-end" |
  "padding-block" | "padding-block-start" | "padding-block-end" |
  "padding-inline" | "padding-inline-start" | "padding-inline-end" |
  "gap" | "row-gap" | "column-gap";

export const mediaQueries = breakpoints.reduce<MediaQueries>((acc, bp, i) => {
  acc[names[i]] = `@media (min-width: ${bp}px)`;
  return acc;
}, {});

// Sizing

// pxToRem: Give it a unitless integer for the px value and it will return
// a rem value (including the "rem", rounded to 3 decimal points)
export const pxToRem = (px : number) => {
  return (Math.floor(px * 1000 / 16) / 1000 + 'rem');
}

// Give it unitless integers for font-size and line-height values in px, and it 
// will return font-size in rem and line-height in a unitless ratio
export const fontSizeLineHeight = (fontSize : number, lineHeight : number) => {
  const fontValue = pxToRem(fontSize);
  const lhValue = Math.floor(lineHeight / fontSize * 1000) / 1000;
  return (`font-size: ${fontValue}; line-height: ${lhValue};`);
}

export const rgbaFromHex = (hex : string, transparency : number) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  return `rgba(${r},${g},${b},${(transparency / 100).toFixed(4)})`;
}

// CSS Transitions 
// Call it without any arguments and it returns '0.3s ease all'.
// Call it with a string and it replace the 'all' with the property you
// named, i.e. transition('color') returns '0.3s ease color'.
// Call it with an array of strings and it concatenates them for you,
// i.e. transition(['color', 'height']) returns '0.3s ease color, 0.3s ease height'.
export const transition = (transitionProps = 'all') => {
  return Array.isArray(transitionProps) ? transitionProps.map(prop => `0.3s ease ${prop}`).join(",") : `0.3s ease ${transitionProps}`;
}

// Font variables
const fonts = {
  primary: `Instrument Sans, sans-serif`,
  secondary: `Space Grotesk, sans-serif`,
}

//Retire soon?
const weights = {
  regular: 400,
  medium: 500,
  semi: 600,
  bold: 700,
  black: 900
}

// Z-index stacking stuff

// Add layers as needed
const zLayers : ZLayer[] = [
  'main',
  'over-main',
  'header',
  'modal',
];

// z(): Give it one of the values of the zLayers array above and it will spit
// out the correct z-index as an integer.
export const z = (layer : ZLayer) => {
  if(layer === "hidden") {
    return -1;
  } else {
    return (zLayers.indexOf(layer) + 1);
  }
}

// Drop Shadows
const dropShadows = {
  small: `0 ${pxToRem(1)} ${pxToRem(4)} 0 ${rgbaFromHex(Neutral1000, 10)}`,
  medium: `0 ${pxToRem(6)} ${pxToRem(24)} 0 ${rgbaFromHex(Neutral1000, 10)}`,
  large: `0 ${pxToRem(8)} ${pxToRem(35)} 0 ${rgbaFromHex(Neutral1000, 15)}`,
  xlarge: `0 ${pxToRem(8)} ${pxToRem(50)} 0 ${rgbaFromHex(Neutral1000, 20)}`
};

const getFontWeight = (token : string) => {
  switch(token) {
    case "Regular":
      return 400;
    case "Medium":
      return 500;
    case "Bold":
      return 700;
    default:
      return 400;
  }
};

const getLetterSpacing = (spacing : string) => {
  const spacingAmmount = parseInt(spacing.replaceAll("%","")) / 100;

  return `${spacingAmmount}em`;
};

const spacing = {
  "mobile": {
    "3xs": PaddingSpacing3Xs,
    "2xs": PaddingSpacing2Xs,
    "xs": PaddingSpacingXs,
    "s": PaddingSpacingS,
    "sm": PaddingSpacingSm,
    "m": PaddingSpacingM,
    "ml": PaddingSpacingMl,
    "l": PaddingSpacingL,
    "xl": PaddingSpacingXl,
    "2xl": PaddingSpacing2Xl,
    "3xl": PaddingSpacing3Xl,
    "4xl": PaddingSpacing4Xl,
    "0": 0
  },
  "tablet": {
    "3xs": PaddingSpacing3XsTablet,
    "2xs": PaddingSpacing2XsTablet,
    "xs": PaddingSpacingXsTablet,
    "s": PaddingSpacingSTablet,
    "sm": PaddingSpacingSmTablet,
    "m": PaddingSpacingMTablet,
    "ml": PaddingSpacingMlTablet,
    "l": PaddingSpacingLTablet,
    "xl": PaddingSpacingXlTablet,
    "2xl": PaddingSpacing2XlTablet,
    "3xl": PaddingSpacing3XlTablet,
    "4xl": PaddingSpacing4XlTablet,
    "0": 0
  },
  "desktop": {
    "3xs": PaddingSpacing3XsDesktop,
    "2xs": PaddingSpacing2XsDesktop,
    "xs": PaddingSpacingXsDesktop,
    "s": PaddingSpacingSDesktop,
    "sm": PaddingSpacingSmDesktop,
    "m": PaddingSpacingMDesktop,
    "ml": PaddingSpacingMlDesktop,
    "l": PaddingSpacingLDesktop,
    "xl": PaddingSpacingXlDesktop,
    "2xl": PaddingSpacing2XlDesktop,
    "3xl": PaddingSpacing3XlDesktop,
    "4xl": PaddingSpacing4XlDesktop,
    "0": 0
  }
}

const respSpacing = (
  cssProp: SpacingProp, 
  token: SpacingToken, 
  inverse: boolean = false
) => {
  // For offsetting margins, sadly an occasional necessity
  const modifier: number = inverse ? -1 : 1;

  return `
    ${cssProp}: ${pxToRem(spacing['mobile'][token] * modifier)};

    ${mediaQueries.md} {
      ${cssProp}: ${pxToRem(spacing['tablet'][token] * modifier)};
    }
    
    ${mediaQueries.xl} {
      ${cssProp}: ${pxToRem(spacing['desktop'][token] * modifier)};
    }
  `
}

const getGridSpacing = (columns: number, offset: number, centered: boolean, desktop: boolean) => {
  const columnGap = desktop ? 40 : 20;
  const innerGridGap = 11 * columnGap;

  const alignmentStyles = centered ? `
    margin-left: auto;
    margin-right: auto;
  ` : `
    margin-left: calc((100% - ${pxToRem(innerGridGap)}) / 12 * ${offset} + ${pxToRem(offset * columnGap)});
    margin-right: unset;
  `;

  return `
    width: calc((100% - ${pxToRem(innerGridGap)}) / 12 * ${columns} + ${pxToRem((columns - 1) * columnGap)});
    ${alignmentStyles};
  `;
}

type colorDict = {
  [key: string]: any
}

const getToken = (theme: UpsunTheme, token: string) => {
  const light : colorDict = colorsLight;
  const dark : colorDict = colorsDark;
  const correctToken = theme.name === "light" ? light[token] : dark[token];

  return correctToken;
};

export { 
  fonts, 
  weights, 
  dropShadows, 
  getFontWeight,
  getLetterSpacing,
  spacing, 
  getGridSpacing, 
  respSpacing,
  getToken,
}